// What each tool costs to run, and how to say that in runs rather than credits.
//
// WHY THIS EXISTS
//
// A credit means nothing on its own. The same balance is 10 site audits or 2
// review analyses, so "5,000 credits" cannot be reasoned about by the person
// deciding whether they can afford to click the button. Runs can.
//
// MIRRORS THE SERVER
//
// The authoritative costs live in
// supabase/functions/_shared/agency-tool-credit-costs.ts, which is what
// actually charges. This file is a display-side mirror, because an edge
// function module cannot be imported into the browser bundle. toolCreditCosts
// .test.ts reads the server file and fails if the two ever disagree, so this
// cannot silently drift the way agencyTiers.ts and razorpay-agency-plans.ts can.

export const TOOL_CREDIT_COSTS: Record<string, number> = {
  'lead-forms': 1,
  'seo-site-audit': 1,
  'website-geo-scan': 1,
  'schema-validator': 1,
  'linkedin-audit': 1,
  'local-rank-check': 1,
  'gbp-posts-score': 1,
  'website-security-audit': 1,
  'gbp-description-generator': 1,
  'instagram-audit': 1,
  'google-posts-health-check': 1,
  'content-calendar-generator': 1,
  'competitor-analysis-nearby': 1,
  'gmb-audit': 1,
  'caption-generator': 1,
  'review-analysis': 1,
  'restaurant-audit': 1,
  'review-response-generator': 1,
  'ai-search-visibility': 7,
};

/** The tool we quote plan allowances against.
 *
 *  Retained for the few surfaces that still phrase an allowance as "about N
 *  Google profile audits". Under scan pricing this is close to redundant: one
 *  run is one scan, so an allowance already IS its run count, and the only
 *  tool that diverges is AI Search Visibility at 7. */
export const HEADLINE_TOOL_SLUG = 'gmb-audit';
export const HEADLINE_TOOL_LABEL = 'scans';

/** How many more times this tool can be run with the balance given.
 *  Returns null when we do not know the tool's cost, so callers can render
 *  nothing rather than a confident wrong number. */
export function runsLeft(
  toolSlug: string,
  creditBalance: number,
  capRemaining?: number | null,
): number | null {
  const cost = TOOL_CREDIT_COSTS[toolSlug];
  if (!cost || cost <= 0) return null;
  const fromBalance = Math.max(0, Math.floor(creditBalance / cost));
  // A per-tool ceiling can bite long before the shared balance does, and the
  // server enforces it. Whichever runs out first is the truth.
  if (typeof capRemaining === 'number') {
    return Math.max(0, Math.min(fromBalance, capRemaining));
  }
  return fromBalance;
}

/**
 * The server's per-tool ceiling for this plan, or null when the tool is
 * uncapped. Mirrors the v_tool_cap CASE in public.agency_charge_credits.
 *
 * Without this the tiles predicted runs from the shared balance alone and
 * over-promised in both directions the server actually caps: a trialing agency
 * was told "About 3 runs left" on AI Search Visibility (25 scans / 7 = 3) when
 * the server allows 2, and a Pro agency was told about 300 runs of Review
 * Analysis when the server stops at 150.
 *
 * KEEP IN STEP with agency_charge_credits. If a cap is added, changed or
 * removed there, change it here too, or the tiles start lying again.
 */
/** The only tools agency_charge_credits applies a per-tool ceiling to. Kept
 *  next to toolRunCap so a new cap cannot be added in one place and forgotten
 *  in the other. Callers use it to scope the "runs used this period" lookup. */
export const CAPPED_TOOL_SLUGS: readonly string[] = [
  'ai-search-visibility',
  'review-analysis',
  'restaurant-audit',
];

export function toolRunCap(
  toolSlug: string,
  planSlug: string | null | undefined,
  status: string | null | undefined,
): number | null {
  // Trial: AI Search costs 7 of the 25 trial scans, so the pool alone would
  // allow 3. The server caps it at 2 so a trialist keeps scans for other tools.
  if (status === 'trialing' && toolSlug === 'ai-search-visibility') return 2;
  // Pro: the two dearest tools to serve, capped below what 300 scans would buy.
  if (planSlug === 'pro' && (toolSlug === 'review-analysis' || toolSlug === 'restaurant-audit')) return 150;
  // Starter has no cap: 100 scans could never reach 150, so a ceiling would be
  // dead code. The server says the same in so many words.
  return null;
}

/** How many runs an allowance buys, for the headline tool. Used on pricing. */
export function headlineRuns(credits: number): number {
  return Math.floor(credits / TOOL_CREDIT_COSTS[HEADLINE_TOOL_SLUG]);
}

/** "about 45 Google profile audits". Deliberately hedged with "about": the
 *  figure is exact for this tool and only indicative for the rest, and an
 *  unqualified number invites people to count. */
export function headlineRunsLabel(credits: number): string {
  return `about ${headlineRuns(credits).toLocaleString()} ${HEADLINE_TOOL_LABEL}`;
}

/** Short form for a tool card: "About 45 runs left", or "No runs left". */
export function runsLeftLabel(
  toolSlug: string,
  creditBalance: number,
  capRemaining?: number | null,
): string | null {
  const n = runsLeft(toolSlug, creditBalance, capRemaining);
  if (n === null) return null;
  if (n === 0) return 'No runs left';
  if (n === 1) return 'About 1 run left';
  return `About ${n.toLocaleString()} runs left`;
}

/** "110 credits per run". The stable half of the label.
 *
 *  This is the number that does NOT move, and it is why the runs-left figure
 *  moving is comprehensible rather than alarming. Every tool draws on one
 *  shared balance, so running an expensive tool visibly reduces how many runs
 *  of a cheap one remain. Shown side by side, that reads as a trade-off the
 *  user made. Shown alone, "about 80 left" silently becoming "about 40 left"
 *  reads as the product losing their scans. */
export function costPerRunLabel(toolSlug: string): string | null {
  const cost = TOOL_CREDIT_COSTS[toolSlug];
  if (!cost) return null;
  // Singular for the ordinary case, which is now every tool but one. "1 scans
  // per run" would be the first thing a user notices and the last thing we
  // want them noticing.
  return cost === 1 ? '1 scan per run' : `${cost.toLocaleString()} scans per run`;
}

/** Tools dear enough that one run visibly moves every other tool's count.
 *  Threshold is a multiple of the headline tool rather than an absolute, so it
 *  tracks pricing changes instead of going stale. */
export function isHeavyTool(toolSlug: string): boolean {
  const cost = TOOL_CREDIT_COSTS[toolSlug];
  if (!cost) return false;
  return cost >= TOOL_CREDIT_COSTS[HEADLINE_TOOL_SLUG] * 4;
}

/** "One run uses about as much as 11 profile audits."
 *
 *  Only for heavy tools, and phrased as a comparison rather than a warning:
 *  the point is to let someone choose knowingly, not to discourage the run. */
export function heavyToolComparison(toolSlug: string): string | null {
  if (!isHeavyTool(toolSlug)) return null;
  const cost = TOOL_CREDIT_COSTS[toolSlug];
  // Under scan pricing the cost IS the comparison: 7 scans is 7 ordinary runs.
  // No division needed, and no rounding to explain away.
  return `One run uses ${cost} scans, the same as ${cost} ordinary audits.`;
}
